import { useState, useEffect } from 'react';
import { MessageCircle, EyeOff } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { api } from '../../services/api';
import type { AdminCourseComments } from '../../types';

export default function AdminComments() {
  const { theme } = useTheme();
  const [courseComments, setCourseComments] = useState<AdminCourseComments[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    setLoading(true);
    try {
      const commentsData = await api.getAllComments();
      setCourseComments(commentsData);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCommentAsSeen = async (commentId: string, courseId: string) => {
    try {
      await api.markCommentAsSeen(commentId);
      setCourseComments(prevComments =>
        prevComments.map(course =>
          course.course_id === courseId
            ? {
                ...course,
                comments: course.comments.filter(comment => comment.id !== commentId)
              }
            : course
        ).filter(course => course.comments.length > 0)
      );
    } catch (error) {
      console.error('Error marking comment as seen:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className={`w-8 h-8 border-4 border-${theme.primary} border-t-transparent rounded-full animate-spin mx-auto mb-4`} />
        <p className="text-gray-600">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">
        Commentaires des cours ({courseComments.reduce((total, course) => total + course.comments.length, 0)})
      </h2>
      {courseComments.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Aucun commentaire trouvé</p>
      ) : (
        courseComments.map((courseComment) => (
          <div key={courseComment.course_id} className="border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-lg mb-4 text-gray-800">{courseComment.title}</h3>
            <div className="space-y-3">
              {courseComment.comments.length === 0 ? (
                <p className="text-gray-500 text-sm italic">Aucun commentaire pour ce cours</p>
              ) : (
                courseComment.comments.map((comment) => (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3 border-l-4 border-rose-400 relative">
                    <div className="flex items-start justify-between mb-2 pr-10">
                      <div className="flex-1">
                        <span className="text-sm text-gray-600">Utilisateur: {comment.user_id}</span>
                        <span className="text-sm text-gray-400 ml-4">{formatDate(comment.date)}</span>
                      </div>
                      <button
                        onClick={() => handleMarkCommentAsSeen(comment.id, courseComment.course_id)}
                        className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-colors"
                        title="Marquer comme vu et masquer"
                      >
                        <EyeOff className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-gray-700 mb-2">{comment.content}</p>
                    {!comment.seen && (
                      <span className="inline-block px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                        Non lu
                      </span>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}